import React, { useState, useRef, useEffect } from 'react';
import { Plus, MapPin, FileText, Package2, X, Clock, Car, MessageSquare, Bot, Loader2 } from 'lucide-react';
import ActiveTimerWarningModal from './ActiveTimerWarningModal';

// Custom speaking indicator component with 3 diagonal lines that respond to audio
const SpeakingIndicator: React.FC<{ className?: string; audioLevel?: number }> = ({ className = "h-5 w-5", audioLevel = 0 }) => {
  // Calculate dynamic effects based on audio level
  const baseHeight = 0.5; // rem - smaller base
  const maxHeight = 1.75; // rem - much taller max
  const heightMultiplier = 1 + (audioLevel * 4); // Scale 1x to 5x based on audio (more dramatic)
  
  // Calculate individual heights with variation
  const height1 = Math.min(baseHeight * heightMultiplier * 0.8, maxHeight * 0.9);
  const height2 = Math.min(baseHeight * heightMultiplier * 1.2, maxHeight);
  const height3 = Math.min(baseHeight * heightMultiplier * 0.9, maxHeight * 0.95);
  
  // Calculate opacity for flashing effect based on audio level
  const minOpacity = 0.4;
  const maxOpacity = 1;
  const opacity = minOpacity + (audioLevel * (maxOpacity - minOpacity));
  
  // Calculate scale for pulsing effect
  const baseScale = 1;
  const maxScale = 1.3;
  const scale = baseScale + (audioLevel * (maxScale - baseScale));
  
  return (
    <div className={`relative ${className}`}>
      {/* Outer glow effect */}
      <div 
        className="absolute inset-0 flex items-center justify-center space-x-0.5 transform rotate-12 transition-all duration-100"
        style={{ 
          transform: `rotate(12deg) scale(${scale})`,
          filter: `blur(1px)`,
          opacity: opacity * 0.6
        }}
      >
        <div 
          className="w-0.5 bg-current rounded-full animate-pulse" 
          style={{ height: `${height1}rem` }}
        ></div>
        <div 
          className="w-0.5 bg-current rounded-full animate-pulse" 
          style={{ height: `${height2}rem`, animationDelay: '150ms' }}
        ></div>
        <div 
          className="w-0.5 bg-current rounded-full animate-pulse" 
          style={{ height: `${height3}rem`, animationDelay: '300ms' }}
        ></div>
      </div>
      
      {/* Main bars */}
      <div 
        className="absolute inset-0 flex items-center justify-center space-x-0.5 transform rotate-12 transition-all duration-75"
        style={{ 
          transform: `rotate(12deg) scale(${scale})`,
          opacity: opacity
        }}
      >
        <div 
          className="w-0.5 bg-current rounded-full transition-all duration-75 animate-pulse" 
          style={{ 
            height: `${height1}rem`,
            animationDuration: '400ms',
            boxShadow: `0 0 4px currentColor`
          }}
        ></div>
        <div 
          className="w-0.5 bg-current rounded-full transition-all duration-75 animate-pulse" 
          style={{ 
            height: `${height2}rem`,
            animationDelay: '100ms',
            animationDuration: '400ms',
            boxShadow: `0 0 6px currentColor`
          }}
        ></div>
        <div 
          className="w-0.5 bg-current rounded-full transition-all duration-75 animate-pulse" 
          style={{ 
            height: `${height3}rem`,
            animationDelay: '200ms',
            animationDuration: '400ms',
            boxShadow: `0 0 4px currentColor`
          }}
        ></div>
      </div>
    </div>
  );
};
import { useNavigate, useLocation } from 'react-router-dom';
import CreateBookingModal from './CreateBookingModal';
import CreateProposalModal from './CreateProposalModal';
import WithdrawStockModal from './WithdrawStockModal';
// Horizontal nav imports - REMOVED

import { useBusiness } from '../contexts/BusinessContext';
import { useAuth } from '../hooks/useAuth';
import { useApplicationSettingsContext } from '../contexts/ApplicationSettingsContext';
import { supabase } from '../lib/supabase';
import { getResources } from '../lib/resources';
import CreateMobileBookingModal from './CreateMobileBookingModal';
import { usePermissionCheck } from '../hooks/usePermissionCheck';
import NexusOverlay from './NexusOverlay';

// NavItem interface - REMOVED (was only used for horizontal nav)

interface FloatingActionButtonProps {
  permissionLevel?: 'manage' | 'view' | 'disabled';
  onSuccess?: () => Promise<void>;
}

export default function FloatingActionButton({ permissionLevel = 'manage', onSuccess }: FloatingActionButtonProps) {
  const { selectedBusiness } = useBusiness();
  const { user } = useAuth();
  const { isPageDisabled } = useApplicationSettingsContext();
  const { canAccess, isAdmin } = usePermissionCheck();
  const navigate = useNavigate();
  const location = useLocation();
  const [showBookingOptions, setShowBookingOptions] = useState(false);
  const [isCreateBookingModalOpen, setIsCreateBookingModalOpen] = useState(false);
  const [isCreateProposalModalOpen, setIsCreateProposalModalOpen] = useState(false);
  const [isWithdrawStockModalOpen, setIsWithdrawStockModalOpen] = useState(false);

  const [clockedIn, setClockedIn] = useState(false);
  const [onBreak, setOnBreak] = useState(false);
  const [currentPayrollEntryId, setCurrentPayrollEntryId] = useState<string | null>(null);
  const [resourceId, setResourceId] = useState<string | null>(null);
  const [elapsedTime, setElapsedTime] = useState('00:00:00');
  const [startTime, setStartTime] = useState<Date | null>(null);
  
  // Active timer warning modal state
  const [isActiveTimerWarningOpen, setIsActiveTimerWarningOpen] = useState(false);
  const [activeTimerBookingTitle, setActiveTimerBookingTitle] = useState<string>('');
  
  // Unviewed missed calls count

  // Function to count unviewed missed calls
  const countUnviewedMissedCalls = async () => {
    if (!selectedBusiness?.id) return;
    
    try {
      const { count, error } = await supabase
        .from('call_history')
        .select('*', { count: 'exact', head: true })
        .eq('business_id', selectedBusiness.id)
        .eq('direction', 'inbound')
        .eq('status', 'missed')
        .eq('viewed', false);

      if (error) {
        console.error('Error counting unviewed missed calls:', error);
      } else {
        const missedCount = count || 0;
        // setUnviewedMissedCount(missedCount); // REMOVED - not used anymore
        console.log(`📞 Mobile call icon badge: ${missedCount} unviewed missed calls`);
      }
    } catch (error) {
      console.error('Error in countUnviewedMissedCalls:', error);
    }
  };

  // Count unviewed missed calls on mount and business change
  useEffect(() => {
    if (selectedBusiness?.id) {
      countUnviewedMissedCalls();
    }
  }, [selectedBusiness?.id]);

  // Set up real-time subscription for call updates to update badge
  useEffect(() => {
    if (!selectedBusiness?.id || !supabase) return;

    const channel = supabase
      .channel(`call_badge_${selectedBusiness.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'call_history',
          filter: `business_id=eq.${selectedBusiness.id}`
        },
        (payload) => {
          console.log('📞 Call update for mobile badge:', payload);
          
          // Check if this is a viewed status change
          if (payload.eventType === 'UPDATE' && payload.new && payload.old) {
            const oldViewed = payload.old.viewed;
            const newViewed = payload.new.viewed;
            
            if (oldViewed === false && newViewed === true) {
              console.log('📞 Call marked as viewed - updating mobile badge');
            }
          }
          
          // Recount when any call changes (INSERT, UPDATE, DELETE)
          countUnviewedMissedCalls();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [selectedBusiness?.id]);
  const [breakStartTime, setBreakStartTime] = useState<Date | null>(null);
  const [compensationType, setCompensationType] = useState<'hourly' | 'flat_rate' | 'commission' | null>(null);
  const fabRef = useRef<HTMLDivElement>(null);
  const [isCreateMobileBookingModalOpen, setIsCreateMobileBookingModalOpen] = useState(false);
  
  
  // Message count state for badge display
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
  
  // AI Assistant state
  const [isNexusOverlayOpen, setIsNexusOverlayOpen] = useState(false);
  const [isNexusThinking, setIsNexusThinking] = useState(false);
  const [isNexusSpeaking, setIsNexusSpeaking] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const stopSpeechRef = useRef<(() => void) | null>(null);
  
  // Track Messages conversation state for mobile FAB hiding
  const [messagesConversationOpen, setMessagesConversationOpen] = useState(false);
  
  // Track Sales Map state for mobile FAB hiding
  const [salesMapOpen, setSalesMapOpen] = useState(false);

  // Listen for localStorage changes to track Messages conversation state
  useEffect(() => {
    const checkMessagesConversationState = () => {
      const isOpen = localStorage.getItem('messages_conversation_open') === 'true';
      setMessagesConversationOpen(isOpen);
    };

    // Check initial state
    checkMessagesConversationState();

    // Listen for storage events (when other tabs change localStorage)
    window.addEventListener('storage', checkMessagesConversationState);
    
    // Listen for custom events (when same tab changes localStorage)
    window.addEventListener('messagesConversationChanged', checkMessagesConversationState);

    return () => {
      window.removeEventListener('storage', checkMessagesConversationState);
      window.removeEventListener('messagesConversationChanged', checkMessagesConversationState);
    };
  }, []);

  // Listen for localStorage changes to track Sales Map state
  useEffect(() => {
    const checkSalesMapState = () => {
      const isOpen = localStorage.getItem('sales_map_open') === 'true';
      setSalesMapOpen(isOpen);
    };

    // Check initial state
    checkSalesMapState();

    // Listen for storage events (when other tabs change localStorage)
    window.addEventListener('storage', checkSalesMapState);
    
    // Listen for custom events (when same tab changes localStorage)
    window.addEventListener('salesMapChanged', checkSalesMapState);

    return () => {
      window.removeEventListener('storage', checkSalesMapState);
      window.removeEventListener('salesMapChanged', checkSalesMapState);
    };
  }, []);

  // Update the checkClockInStatus function to also get compensation type
  useEffect(() => {
    const checkClockInStatus = async () => {
      if (!selectedBusiness?.id || !user?.id || !supabase) return;

      try {
        // First get the resource ID and compensation type for the authenticated user
        const { data: resourceData, error: resourceError } = await supabase
          .from('resources')
          .select('id, compensation_type')
          .eq('auth_user_id', user.id)
          .eq('business_id', selectedBusiness.id)
          .single();

        if (resourceError) throw resourceError;
        if (!resourceData) return;

        setResourceId(resourceData.id);
        setCompensationType(resourceData.compensation_type);

        // Check if already clocked in
        const { data, error } = await supabase
          .from('payroll_page')
          .select('id, clock_in, break_start, break_end')
          .eq('resource_id', resourceData.id)
          .eq('business_id', selectedBusiness.id)
          .eq('entry_type', 'timeclock')
          .is('clock_out', null)
          .maybeSingle();

        if (error) throw error;

        if (data) {
          setClockedIn(true);
          setCurrentPayrollEntryId(data.id);
          setStartTime(new Date(data.clock_in));
          if (data.break_start && !data.break_end) {
            setOnBreak(true);
            setBreakStartTime(new Date(data.break_start));
          }
        }
      } catch (error) {
        console.error('Error checking clock in status:', error);
      }
    };

    checkClockInStatus();
  }, [selectedBusiness?.id, user?.id]);

  // Add timer effect
  useEffect(() => {
    let intervalId: NodeJS.Timeout;
    
    if (clockedIn && startTime) {
      intervalId = setInterval(() => {
        const now = new Date();
        let diff = now.getTime() - startTime.getTime();
        
        // Subtract break time if on break
        if (onBreak && breakStartTime) {
          diff = diff - (now.getTime() - breakStartTime.getTime());
        }
        
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);
        
        setElapsedTime(
          `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
        );
      }, 1000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [clockedIn, startTime, onBreak, breakStartTime]);

  // Fetch initial unread message count and set up realtime subscription for badge display
  useEffect(() => {
    if (!selectedBusiness?.id || !supabase) return;

    // Function to fetch current unread count
    const fetchUnreadCount = async () => {
      try {
        if (!supabase) {
          console.error('[FAB BADGE] Supabase client not initialized');
          return;
        }

        const { count, error } = await supabase
          .from('all_messages')
          .select('*', { count: 'exact', head: true })
          .eq('business_id', selectedBusiness.id)
          .eq('read', 'unread')
          .eq('direction', 'inbound'); // Only count inbound unread messages

        if (error) {
          console.error('[FAB BADGE] Error fetching unread count:', error);
          return;
        }

        const newCount = count || 0;
        console.log('[FAB BADGE] 🔢 Updating badge count:', newCount);
        setUnreadMessagesCount(newCount);
      } catch (error) {
        console.error('[FAB BADGE] Error fetching unread count:', error);
      }
    };

    // Fetch initial count
    fetchUnreadCount();

    console.log('[FAB BADGE] 🔄 Setting up FAB navigation badge subscription for business:', selectedBusiness.id);

    // Set up realtime subscription with more specific event handling
    const subscription = supabase
      .channel(`unread_messages_fab_badge_${selectedBusiness.id}`)
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to all events (INSERT, UPDATE, DELETE)
          schema: 'public',
          table: 'all_messages',
          filter: `business_id=eq.${selectedBusiness.id}` // Only listen to messages for this business
        },
        (payload) => {
          console.log('[FAB BADGE] 📨 Received database event:', {
            eventType: payload.eventType,
            messageId: (payload.new as any)?.id || (payload.old as any)?.id,
            direction: (payload.new as any)?.direction || (payload.old as any)?.direction,
            read: (payload.new as any)?.read || (payload.old as any)?.read
          });
          
          // Add a small delay to ensure database consistency
          setTimeout(() => {
            fetchUnreadCount();
          }, 100);
        }
      )
      .subscribe((status) => {
        console.log('[FAB BADGE] 📡 Subscription status:', status);
        
        if (status === 'SUBSCRIBED') {
          console.log('[FAB BADGE] ✅ FAB navigation badge subscription active');
        }
      });

    // Set up a periodic refresh as backup (every 30 seconds)
    const periodicRefresh = setInterval(() => {
      console.log('[FAB BADGE] 🔄 Periodic refresh of badge count');
      fetchUnreadCount();
    }, 30000);

    // Listen for custom events from Messages page for immediate updates
    const handleUnreadMessagesChanged = () => {
      console.log('[FAB BADGE] 📢 Received local update event, refreshing badge');
      fetchUnreadCount();
    };

    window.addEventListener('unreadMessagesChanged', handleUnreadMessagesChanged);

    // Cleanup subscription on unmount or business change
    return () => {
      console.log('[FAB BADGE] 🧹 Cleaning up FAB navigation badge subscription');
      subscription.unsubscribe();
      clearInterval(periodicRefresh);
      window.removeEventListener('unreadMessagesChanged', handleUnreadMessagesChanged);
    };
  }, [selectedBusiness?.id]);

  const handleClockIn = async () => {
    if (!selectedBusiness?.id || !user?.id || !supabase || !resourceId) return;

    try {
      const now = new Date();
      // Get resource's details for rate information
      const { data: resourceData, error: resourceError } = await supabase
        .from('resources')
        .select('hourly_rate, compensation_type')
        .eq('id', resourceId)
        .single();

      if (resourceError) throw resourceError;
      if (!resourceData) throw new Error('Resource not found');

      // Create the payroll entry with all required fields
      const { data, error } = await supabase
        .from('payroll_page')
        .insert({
          business_id: selectedBusiness.id,
          resource_id: resourceId,
          clock_in: now.toISOString(),
          entry_type: 'timeclock',
          status: 'active',
          process_status: 'UNPAID',
          compensation_type: resourceData.compensation_type || 'hourly',
          rate_amount: resourceData.hourly_rate || 0,
          total_hours: 0,
          total_minutes: 0,
          total_earnings: 0
        })
        .select()
        .single();

      if (error) throw error;

      setClockedIn(true);
      setCurrentPayrollEntryId(data.id);
      setStartTime(now);
      setShowBookingOptions(false);
    } catch (error) {
      console.error('Error clocking in:', error);
      alert('Failed to clock in. Please try again.');
    }
  };

  const handleClockOut = async () => {
    if (!currentPayrollEntryId || !supabase || !resourceId || !startTime) return;

    // Check for active timers before allowing clock out
    try {
      const { data: activeTimerLogs, error: timerError } = await supabase
        .from('booking_timer_logs')
        .select(`
          booking_id,
          bookings!inner(title)
        `)
        .eq('resource_id', resourceId)
        .eq('is_running', true)
        .limit(1);

      if (timerError) {
        console.error('Error checking for active timers:', timerError);
      } else if (activeTimerLogs && activeTimerLogs.length > 0) {
        // Active timer found, show warning modal
        const bookingTitle = activeTimerLogs[0].bookings?.title || 'Unknown Booking';
        setActiveTimerBookingTitle(bookingTitle);
        setIsActiveTimerWarningOpen(true);
        return; // Don't proceed with clock out
      }
    } catch (error) {
      console.error('Error checking for active timers:', error);
    }

    try {
      const now = new Date();

      // Get the current entry to calculate totals
      const { data: currentEntry, error: fetchError } = await supabase
        .from('payroll_page')
        .select('*, resource:resources(hourly_rate)')
        .eq('id', currentPayrollEntryId)
        .single();

      if (fetchError) throw fetchError;
      if (!currentEntry) throw new Error('Entry not found');

      // Calculate total minutes worked
      let totalMs = now.getTime() - startTime.getTime();
      
      // Get all completed breaks for this entry
      const { data: breaks, error: breaksError } = await supabase
        .from('payroll_page')
        .select('break_start, break_end')
        .eq('id', currentPayrollEntryId)
        .not('break_start', 'is', null)
        .not('break_end', 'is', null);

      if (breaksError) throw breaksError;

      // Subtract all completed break times
      if (breaks) {
        breaks.forEach(breakPeriod => {
          const breakStart = new Date(breakPeriod.break_start);
          const breakEnd = new Date(breakPeriod.break_end);
          totalMs -= (breakEnd.getTime() - breakStart.getTime());
        });
      }

      // Calculate hours and earnings
      const totalMinutes = Math.round(totalMs / (1000 * 60));
      const hours = totalMinutes / 60;
      const hourlyRate = currentEntry.resource?.hourly_rate || 0;
      const totalEarnings = hours * hourlyRate;

      // Update the entry with final calculations
      const { error: updateError } = await supabase
        .from('payroll_page')
        .update({
          clock_out: now.toISOString(),
          total_minutes: totalMinutes,
          total_hours: hours,
          total_earnings: totalEarnings,
          status: 'completed'
        })
        .eq('id', currentPayrollEntryId)
        .eq('resource_id', resourceId);

      if (updateError) throw updateError;

      setClockedIn(false);
      setOnBreak(false);
      setCurrentPayrollEntryId(null);
      setStartTime(null);
      setBreakStartTime(null);
      setShowBookingOptions(false);
    } catch (error) {
      console.error('Error clocking out:', error);
      alert('Failed to clock out. Please try again.');
    }
  };

  const handleBreak = async () => {
    if (!currentPayrollEntryId || !supabase || !resourceId) return;

    try {
      const now = new Date();
      if (!onBreak) {
        // Start break
        const { error } = await supabase
          .from('payroll_page')
          .update({
            break_start: now.toISOString()
          })
          .eq('id', currentPayrollEntryId)
          .eq('resource_id', resourceId);

        if (error) throw error;
        setOnBreak(true);
        setBreakStartTime(now);
      } else {
        // End break
        const { error } = await supabase
          .from('payroll_page')
          .update({
            break_end: now.toISOString()
          })
          .eq('id', currentPayrollEntryId)
          .eq('resource_id', resourceId);

        if (error) throw error;
        setOnBreak(false);
        setBreakStartTime(null);
      }
      setShowBookingOptions(false);
    } catch (error) {
      console.error('Error handling break:', error);
      alert('Failed to update break status. Please try again.');
    }
  };

  // Handle click outside for dropdown
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (fabRef.current && !fabRef.current.contains(event.target as Node)) {
        setShowBookingOptions(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSuccess = async () => {
    if (onSuccess) {
      await onSuccess();
    }
    setShowBookingOptions(false);
    
    // Dispatch custom event to refresh mobile calendar
    window.dispatchEvent(new CustomEvent('mobileBookingCreated'));
  };

  // Nexus AI handlers
  const handleNexusToggle = () => {
    if (isNexusOverlayOpen) {
      // Close the overlay and deactivate Nexus
      if (stopSpeechRef.current) {
        stopSpeechRef.current();
      }
      setIsNexusOverlayOpen(false);
      // setIsNexusActivated(false); // REMOVED - not used anymore
    } else {
      // Open the overlay and activate Nexus
      setIsNexusOverlayOpen(true);
      // setIsNexusActivated(true); // REMOVED - not used anymore
    }
  };



  // Horizontal navigation handlers - REMOVED


  // Long press handlers for edit mode
  // Long press and edit mode functions - REMOVED (were only used for horizontal nav)

  useEffect(() => {
    if (selectedBusiness?.id) {
      const fetchResources = async () => {
        try {
          await getResources(selectedBusiness.id);
          // Resources fetching - REMOVED (not used anymore)
        } catch (error) {
          console.error('Error fetching resources:', error);
        }
      };
      fetchResources();
    }
  }, [selectedBusiness?.id]);

  // Navigation items functions - REMOVED (were only used for horizontal nav)

  // Load horizontal navigation config - REMOVED

  // Cleanup - REMOVED (was only for long press timer)

  return (
    <div>
      <div className={`fixed bottom-6 right-6 ${isNexusOverlayOpen ? 'z-[60]' : 'z-50'} ${
        // Hide FAB on mobile when on Messages page with conversation open or Sales Map is open
        (location.pathname === '/messages' && messagesConversationOpen) ||
        (location.pathname === '/marketing' && salesMapOpen)
          ? 'hidden md:block' 
          : 'block'
      }`}>
        <div ref={fabRef}>
          {/* Nexus Controls - Desktop Only - Above FAB - Admin Only */}
          {isAdmin && (
            <div className="hidden md:block absolute bottom-full mb-4 right-0 flex items-center space-x-2">
              {/* Main Nexus Icon */}
              <button
                onClick={handleNexusToggle}
                className={`backdrop-blur-xl border border-white/20 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 p-3 hover:scale-105 ${
                  isNexusOverlayOpen 
                    ? 'bg-white/90 text-purple-600' 
                    : 'bg-white/70 text-gray-800 hover:bg-white/90'
                }`}
                style={{ 
                  backdropFilter: 'blur(20px)', 
                  WebkitBackdropFilter: 'blur(20px)'
                }}
                title="Nexus AI Assistant"
              >
                {isNexusThinking ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : isNexusSpeaking ? (
                  <SpeakingIndicator className="h-5 w-5" audioLevel={audioLevel} />
                ) : (
                  <Bot className="h-5 w-5" />
                )}
              </button>
            </div>
          )}

          <div className="relative">
            <button 
              disabled={permissionLevel === 'view'}
              onClick={() => {
                setShowBookingOptions(!showBookingOptions);
              }}
              className={`fab-button backdrop-blur-xl bg-gradient-to-br from-blue-500 to-blue-700 border border-white/20 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 p-3 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed ${
                showBookingOptions ? 'from-blue-600 to-blue-800 scale-105' : ''
              }`}
              style={{ 
                backdropFilter: 'blur(20px)', 
                WebkitBackdropFilter: 'blur(20px)',
                boxShadow: '0 8px 32px rgba(59, 130, 246, 0.3)'
              }}
            >
              <Plus className={`fab-button-icon h-5 w-5 transition-transform duration-300 ${showBookingOptions ? 'rotate-45' : ''}`} />
            </button>
            

          </div>
          
          {/* Permanent Messages Button - Mobile Only */}
          {!isPageDisabled('messages') && canAccess.messages && location.pathname !== '/messages' && location.pathname !== '/calls-beta' && (
            <div className={`
              md:hidden absolute bottom-14 left-1/2 transform -translate-x-1/2 transition-all duration-300 ease-in-out
              ${showBookingOptions
                ? 'opacity-0 scale-95 pointer-events-none invisible'
                : 'opacity-100 scale-100 pointer-events-auto visible'}
            `}>
              <div className="relative">
                <button
                  onClick={() => navigate('/messages')}
                  className="backdrop-blur-xl bg-gradient-to-br from-green-500 to-green-600 border border-white/20 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 p-3.5 hover:scale-105"
                  style={{ 
                    backdropFilter: 'blur(20px)', 
                    WebkitBackdropFilter: 'blur(20px)',
                    boxShadow: '0 8px 32px rgba(34, 197, 94, 0.3)'
                  }}
                >
                  <MessageSquare className="h-5 w-5" />
                </button>
                
                {/* Unread messages badge - always show when count > 0 */}
                {unreadMessagesCount > 0 && (
                  <div className="absolute -top-1 -right-1 flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-500 rounded-full shadow-lg border-2 border-white/20">
                    {unreadMessagesCount > 99 ? '99+' : unreadMessagesCount}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Permanent Calls Button - Mobile Only - REMOVED: Now handled by dial pad icon in top navigation */}

          {/* Nexus Controls - Removed from mobile FAB menu */}
          
          {/* Horizontal Navigation - REMOVED: No longer needed */}
          
          {/* Quick Actions - Redesigned to match horizontal nav style */}
          <div className={`
            md:flex absolute bottom-full -right-3 mb-6 flex-col items-end space-y-3 transform transition-all duration-300 ease-in-out
            ${showBookingOptions 
              ? 'opacity-100 translate-y-0 scale-100 pointer-events-auto visible' 
              : 'opacity-0 translate-y-2 scale-95 pointer-events-none invisible'}
          `}>
            
            {/* Timeclock Section - Special handling for hourly resources */}
            {compensationType === 'hourly' && (
              <div className="flex flex-col items-end space-y-2">
                {clockedIn ? (
                  <>
                    {/* Timer Display */}
                    <div className="backdrop-blur-xl bg-white/80 border border-white/20 rounded-xl shadow-lg px-3 py-2 text-right"
                         style={{ 
                           backdropFilter: 'blur(20px)', 
                           WebkitBackdropFilter: 'blur(20px)'
                         }}>
                      <div className="text-xs text-gray-600">Time Elapsed</div>
                      <div className="text-sm font-medium text-gray-900">{elapsedTime}</div>
                    </div>
                    
                    {/* Clock Out and Break buttons */}
                    <div className="flex items-center space-x-3">
                      <div className="relative">
                        <button
                          onClick={handleBreak}
                          className={`backdrop-blur-xl border border-white/20 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 p-3 hover:scale-105 ${
                            onBreak ? 'bg-gradient-to-br from-yellow-500 to-yellow-600' : 'bg-gradient-to-br from-blue-500 to-blue-600'
                          }`}
                          style={{ 
                            backdropFilter: 'blur(20px)', 
                            WebkitBackdropFilter: 'blur(20px)',
                            boxShadow: `0 8px 32px ${onBreak ? 'rgba(234, 179, 8, 0.3)' : 'rgba(59, 130, 246, 0.3)'}`
                          }}
                          title={onBreak ? 'End Break' : 'Start Break'}
                        >
                          <Clock className="h-5 w-5" />
                        </button>
                      </div>
                      
                      <div className="relative">
                        <button
                          onClick={handleClockOut}
                          className="backdrop-blur-xl bg-gradient-to-br from-red-500 to-red-600 border border-white/20 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 p-3 hover:scale-105"
                          style={{ 
                            backdropFilter: 'blur(20px)', 
                            WebkitBackdropFilter: 'blur(20px)',
                            boxShadow: '0 8px 32px rgba(239, 68, 68, 0.3)'
                          }}
                          title="Clock Out"
                        >
                          <X className="h-5 w-5" />
                        </button>

                      </div>
                    </div>
                  </>
                ) : (
                  <button
                    onClick={handleClockIn}
                    className="backdrop-blur-xl bg-gradient-to-br from-green-500 to-green-600 border border-white/20 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 p-3 hover:scale-105"
                    style={{ 
                      backdropFilter: 'blur(20px)', 
                      WebkitBackdropFilter: 'blur(20px)',
                      boxShadow: '0 8px 32px rgba(34, 197, 94, 0.3)'
                    }}
                    title="Clock In"
                  >
                    <Clock className="h-5 w-5" />
                  </button>
                )}
              </div>
            )}

            {/* Quick Action Buttons */}
            <div className="flex flex-col items-end space-y-3">
              {/* Location Booking */}
              {canAccess.calendar && !isPageDisabled('calendarV2') && (
                <button
                  onClick={() => {
                    setIsCreateBookingModalOpen(true);
                    setShowBookingOptions(false);
                  }}
                  className="flex items-center backdrop-blur-xl bg-white/80 hover:bg-white/90 border border-white/20 rounded-2xl pr-3 pl-4 py-2 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 group"
                  style={{ 
                    backdropFilter: 'blur(20px)', 
                    WebkitBackdropFilter: 'blur(20px)'
                  }}
                >
                  <span className="text-sm font-medium text-gray-800 mr-3">
                    Location Booking
                  </span>
                  <div className="backdrop-blur-sm bg-gradient-to-br from-blue-500 to-blue-700 text-white rounded-full p-3 shadow-md group-hover:from-blue-600 group-hover:to-blue-800 transition-all duration-300"
                       style={{ 
                         backdropFilter: 'blur(10px)', 
                         WebkitBackdropFilter: 'blur(10px)'
                       }}>
                    <MapPin className="h-5 w-5" />
                  </div>
                </button>
              )}

              {/* Mobile Booking */}
              {canAccess.calendar && !isPageDisabled('mobile-calendar') && (
                <button
                  onClick={() => {
                    setIsCreateMobileBookingModalOpen(true);
                    setShowBookingOptions(false);
                  }}
                  className="flex items-center backdrop-blur-xl bg-white/80 hover:bg-white/90 border border-white/20 rounded-2xl pr-3 pl-4 py-2 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 group"
                  style={{ 
                    backdropFilter: 'blur(20px)', 
                    WebkitBackdropFilter: 'blur(20px)'
                  }}
                >
                  <span className="text-sm font-medium text-gray-800 mr-3">
                    Mobile Booking
                  </span>
                  <div className="backdrop-blur-sm bg-gradient-to-br from-blue-500 to-blue-700 text-white rounded-full p-3 shadow-md group-hover:from-blue-600 group-hover:to-blue-800 transition-all duration-300"
                       style={{ 
                         backdropFilter: 'blur(10px)', 
                         WebkitBackdropFilter: 'blur(10px)'
                       }}>
                    <Car className="h-5 w-5" />
                  </div>
                </button>
              )}

              {/* Proposal */}
              {!isPageDisabled('proposals') && (
                <button
                  onClick={() => {
                    setIsCreateProposalModalOpen(true);
                    setShowBookingOptions(false);
                  }}
                  className="flex items-center backdrop-blur-xl bg-white/80 hover:bg-white/90 border border-white/20 rounded-2xl pr-3 pl-4 py-2 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 group"
                  style={{ 
                    backdropFilter: 'blur(20px)', 
                    WebkitBackdropFilter: 'blur(20px)'
                  }}
                >
                  <span className="text-sm font-medium text-gray-800 mr-3">
                    Proposal
                  </span>
                  <div className="backdrop-blur-sm bg-gradient-to-br from-blue-500 to-blue-700 text-white rounded-full p-3 shadow-md group-hover:from-blue-600 group-hover:to-blue-800 transition-all duration-300"
                       style={{ 
                         backdropFilter: 'blur(10px)', 
                         WebkitBackdropFilter: 'blur(10px)'
                       }}>
                    <FileText className="h-5 w-5" />
                  </div>
                </button>
              )}

              {/* Withdraw Stock */}
              {!isPageDisabled('supplies') && (
                <button
                  onClick={() => {
                    setIsWithdrawStockModalOpen(true);
                    setShowBookingOptions(false);
                  }}
                  className="flex items-center backdrop-blur-xl bg-white/80 hover:bg-white/90 border border-white/20 rounded-2xl pr-3 pl-4 py-2 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 group"
                  style={{ 
                    backdropFilter: 'blur(20px)', 
                    WebkitBackdropFilter: 'blur(20px)'
                  }}
                >
                  <span className="text-sm font-medium text-gray-800 mr-3">
                    Withdraw Stock
                  </span>
                  <div className="backdrop-blur-sm bg-gradient-to-br from-blue-500 to-blue-700 text-white rounded-full p-3 shadow-md group-hover:from-blue-600 group-hover:to-blue-800 transition-all duration-300"
                       style={{ 
                         backdropFilter: 'blur(10px)', 
                         WebkitBackdropFilter: 'blur(10px)'
                       }}>
                    <Package2 className="h-5 w-5" />
                  </div>
                </button>
              )}
            </div>
          </div>
        
        </div>
      </div>

      <CreateBookingModal
        isOpen={isCreateBookingModalOpen}
        onClose={() => setIsCreateBookingModalOpen(false)}
        onSuccess={async () => {
          setIsCreateBookingModalOpen(false);
          await handleSuccess();
        }}
      />

      <CreateProposalModal
        isOpen={isCreateProposalModalOpen}
        onClose={() => setIsCreateProposalModalOpen(false)}
        onSuccess={async () => {
          setIsCreateProposalModalOpen(false);
          await handleSuccess();
        }}
      />

      <WithdrawStockModal
        isOpen={isWithdrawStockModalOpen}
        onClose={() => setIsWithdrawStockModalOpen(false)}
        onSuccess={async () => {
          setIsWithdrawStockModalOpen(false);
          await handleSuccess();
        }}
      />



      <CreateMobileBookingModal
        isOpen={isCreateMobileBookingModalOpen}
        onClose={() => setIsCreateMobileBookingModalOpen(false)}
        onSuccess={async () => {
          setIsCreateMobileBookingModalOpen(false);
          await handleSuccess();
        }}
      />

      {/* HorizontalNavConfigModal - REMOVED */}

      {/* Nexus Overlay - Admin Only */}
      {isAdmin && (
        <NexusOverlay 
          isOpen={isNexusOverlayOpen} 
          onClose={() => setIsNexusOverlayOpen(false)}
          stopSpeechRef={stopSpeechRef}
          onThinkingStateChange={setIsNexusThinking}
          onSpeechStateChange={setIsNexusSpeaking}
          onAudioLevelChange={setAudioLevel}
          onBookingUpdate={handleSuccess}
        />
      )}

      {/* Active Timer Warning Modal */}
      <ActiveTimerWarningModal
        isOpen={isActiveTimerWarningOpen}
        onClose={() => setIsActiveTimerWarningOpen(false)}
        bookingTitle={activeTimerBookingTitle}
      />
    </div>
  );
} 